<template>
  <div class="w-full h-full min-h-[100svh] relative overflow-hidden bg-primary-50/30 flex flex-col">
    <!-- Header -->
    <div class="absolute top-0 left-0 right-0 z-20 bg-white/85 backdrop-blur-md border-b border-primary-100">
      <div class="w-full p-4 flex items-center justify-center">
        <h1 class="font-bold title-color text-lg">Fournir votre adresse</h1>
      </div>
    </div>

    <!-- Map Background (aesthetic) -->
    <div class="flex-1 w-full h-full">
      <Map ref="mapRef" class="w-full h-full pointer-events-none opacity-50 grayscale transition-all duration-700" :class="{'opacity-100 grayscale-0': mapReady}" @ready="onMapReady" />
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="absolute inset-0 z-30 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center">
      <div class="relative w-16 h-16 flex items-center justify-center">
        <div class="absolute w-full h-full border-4 border-primary-200 rounded-full"></div>
        <div class="absolute w-full h-full border-4 border-primary-500 rounded-full border-t-transparent animate-spin"></div>
        <svg class="w-6 h-6 text-primary-500 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
        </svg>
      </div>
      <p class="mt-4 text-primary-700 font-bold animate-pulse">Chargement en cours...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="errorMsg" class="absolute inset-0 z-30 bg-white flex flex-col items-center justify-center p-6 text-center">
      <div class="w-20 h-20 bg-primary-50 rounded-full flex items-center justify-center mb-6">
        <svg class="h-10 w-10 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 class="text-2xl font-black title-color mb-2">Lien invalide</h2>
      <p class="text-primary-600 mb-8 max-w-sm">{{ errorMsg }}</p>
      <button @click="backHome" class="px-8 py-3.5 bg-primary-100 text-primary-700 font-bold rounded-2xl hover:bg-primary-200 hover:title-color transition-all active:scale-95 ">
        Retourner à l'accueil
      </button>
    </div>

    <!-- Success State -->
    <div v-else-if="isSuccess" class="absolute inset-0 z-30 bg-white flex flex-col items-center justify-center p-6 text-center">
      <div class="w-24 h-24 bg-primary-50 rounded-[2rem] flex items-center justify-center mb-6 scale-in" style="animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;">
        <svg class="h-12 w-12 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 class="text-2xl font-black title-color mb-3">Adresse transmise !</h2>
      <p class="text-primary-600 mb-10 max-w-sm leading-relaxed">
        Merci ! Votre position a été enregistrée dans le carnet d'adresses de 
        <span class="font-bold text-primary-800 bg-primary-50 px-2 py-0.5 rounded-lg ml-1">{{ requestInfo?.sender?.firstName }} {{ requestInfo?.sender?.lastName }}</span>.
      </p>
      <button @click="backHome" class="w-full max-w-xs py-4 bg-primary-500 text-white rounded-2xl font-extrabold  hover:-translate-y-0.5 transition-all duration-300 active:scale-95">
        Découvrir Rapidoo
      </button>
    </div>

    <!-- Main Content Bottom Sheet -->
    <div v-else class="absolute bottom-0 left-0 right-0 z-20 flex flex-col max-h-[85%] slide-up">
      <div class="bg-white pt-8 pb-8 px-6 rounded-t-[2.5rem] flex-1 overflow-auto border-t border-primary-100">
        <!-- Drag Handle Indicator -->
        <div class="absolute top-3 left-1/2 -translate-x-1/2 w-12 h-1.5 bg-primary-200 rounded-full"></div>

        <div class="text-center space-y-6">
          <!-- Sender info -->
          <div class="relative w-20 h-20 mx-auto">
            <div class="absolute inset-0 bg-primary-500 rounded-full animate-spin-slow opacity-20 blur-md"></div>
            <div class="relative w-full h-full rounded-2xl bg-white  flex items-center justify-center border border-primary-100 overflow-hidden" style="border-radius: 24px;">
              <template v-if="requestInfo?.sender?.metadata?.avatar || requestInfo?.sender?.avatar">
                <img :src="requestInfo?.sender?.metadata?.avatar || requestInfo?.sender?.avatar" alt="Avatar" class="w-full h-full object-cover">
              </template>
              <template v-else>
                <span class="text-3xl text-primary-300 font-black tracking-tighter">{{ requestInfo?.sender?.firstName?.[0] || '?' }}</span>
              </template>
            </div>
            <!-- Indicator Badge -->
            <div class="absolute -bottom-2 -right-2 w-8 h-8 bg-primary-500 border-4 border-white rounded-full flex items-center justify-center ">
              <svg class="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
            </div>
          </div>
          
          <div class="space-y-1">
            <h2 class="text-xl font-black title-color tracking-tight">{{ requestInfo?.sender?.firstName }} {{ requestInfo?.sender?.lastName }}</h2>
            <p class="text-sm font-medium text-primary-600 bg-primary-50 inline-block px-3 py-1 rounded-lg">souhaite enregistrer votre position</p>
          </div>

          <div class="bg-primary-50/60 border border-primary-100 p-5 rounded-2xl text-left relative overflow-hidden group  transition-all duration-300">
            <div class="absolute -right-6 -top-6 w-24 h-24 bg-primary-500/5 rounded-full group-hover:scale-150 transition-transform duration-700"></div>
            <div class="flex gap-4 relative z-10">
              <div class="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0 text-primary-600">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p class="text-sm title-color font-medium leading-relaxed pt-0.5">
                Ceci permettra d'organiser plus facilement et plus rapidement vos futures expéditions / livraisons de colis avec lui.
              </p>
            </div>
          </div>

          <!-- Select Position Button -->
          <button
            @click="openLocationPicker"
            class="w-full relative group py-4.5 flex items-center justify-center gap-3 bg-primary-500 text-white rounded-[1.25rem] font-bold  hover:-translate-y-0.5 active:translate-y-0.5 active:scale-95 transition-all duration-300"
          >
            <div class="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
              </svg>
            </div>
            Partager ma position
            <div class="absolute inset-0 rounded-[1.25rem] bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import Map from '@features/map/Map.vue';

const route = useRoute();
const router = useRouter();
const BASE_URL = 'https://api.monrapidoo.com';
const GROUP_QUERY = 'group=ultraxeno';

const isLoading = ref(true);
const isSuccess = ref(false);
const errorMsg = ref('');
const requestInfo = ref(null);
const mapReady = ref(false);

const token = route.params.token;

let locationHandlerId;

onMounted(async () => {
  if (!token) {
    errorMsg.value = "Le lien fourni est incomplet ou invalide.";
    isLoading.value = false;
    return;
  }

  try {
    const requestResponse = await fetch(`${BASE_URL}/location/request/${token}?${GROUP_QUERY}`);
    const requestResult = await requestResponse.json();

    if (!requestResponse.ok) {
      throw new Error(requestResult.message || "Impossible de récupérer les détails de la demande.");
    }

    if (requestResult.data.status === 'fulfilled') {
      errorMsg.value = "Cette demande d'adresse a déjà été satisfaite.";
      isLoading.value = false;
      return;
    }

    const requestData = requestResult.data;
    const senderId = requestData.senderId;

    let senderDetails = null;
    if (senderId) {
      try {
        const userResponse = await fetch(`${BASE_URL}/user/rapidoo/${senderId}?${GROUP_QUERY}`);
        const userResult = await userResponse.json();
        if (userResponse.ok && userResult.data) {
          senderDetails = userResult.data;
        }
      } catch (userErr) {
        console.warn("[RequestLocationPage] Impossible de récupérer les détails du sender:", userErr);
      }
    }

    requestInfo.value = {
      ...requestData,
      sender: senderDetails
    };

  } catch (err) {
    errorMsg.value = err.message || "Une erreur est survenue lors de la communication avec le serveur.";
  } finally {
    isLoading.value = false;
  }

  locationHandlerId = bus.on(constants.EVENTS.MAP.LOCATION_SELECTED, handleLocationSelected);
});

onUnmounted(() => {
  if (locationHandlerId) {
    bus.off(locationHandlerId);
  }
});

function onMapReady() {
  mapReady.value = true;
}

function openLocationPicker() {
  bus.emit(constants.EVENTS.MAP.LOCATION_SELECTION_REQUIRED, {
    requestKey: 'public_location_request_fulfill',
    additionalFields: [
      {
        key: 'contactName',
        label: 'Votre nom complet',
        type: 'text',
        required: true,
        userDefaultValue: ''
      },
      {
        key: 'contactPhone',
        label: 'Numéro de téléphone',
        type: 'tel',
        required: true,
        userDefaultValue: ''
      }
    ]
  });
}

async function handleLocationSelected({ requestKey, selectedLocation }) {
  if (requestKey !== 'public_location_request_fulfill') return;

  isLoading.value = true;
  
  try {
    const payload = {
      id: token,
      latitude: selectedLocation.lat,
      longitude: selectedLocation.lng,
      formattedAddress: selectedLocation.address,
      locationDescription: selectedLocation.description || '',
      accuracy: selectedLocation.accuracy || 0
    };

    const response = await fetch(`${BASE_URL}/location/request/fulfill?${GROUP_QUERY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || "Une erreur est survenue lors de l'enregistrement.");
    }

    isSuccess.value = true;
  } catch (err) {
    console.error('[RequestLocationPage] Submission Error:', err);
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: err.message || "Impossible de sauvegarder l'adresse."
    });
  } finally {
    isLoading.value = false;
  }
}

function backHome() {
  router.push('/');
}
</script>

<style scoped>
@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
}
.scale-in {
  animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}
.slide-up {
  animation: slideUp 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}
@keyframes slideUp {
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-spin-slow {
  animation: spin 8s linear infinite;
}
</style>